// =========================================================================
// Sensitive Feature Access — granular, per-user permission layer
//
// A named-feature grant system that sits ABOVE role. Even a `wtc_admin` can
// see a sensitive feature (e.g. Plaid financial data) ONLY if the feature key
// is present in their `app_users.sensitive_feature_access` array. Default-deny
// for everyone; grants are managed MANUALLY in Supabase (service-role) — there
// is deliberately no user-facing write path.
//
// This module is the SINGLE SOURCE OF TRUTH for:
//   - which feature keys exist (FEATURE_KEYS / SENSITIVE_FEATURES)
//   - the typed gate helper (hasFeatureAccess) — the `feature` arg is the
//     SensitiveFeature union, so a typo can never compile
//   - boundary validation of the raw DB `string[]` (parseGrantedFeatures)
//
// See engineering rule SENSITIVE_FEATURE_ACCESS for how/where to apply it.
// =========================================================================

import { z } from "zod";

/**
 * Canonical list of sensitive feature keys. Add a new sensitive feature by
 * adding its key here (and documenting it) — everything else (union type, zod
 * schema, gate helper) derives from this tuple. No migration is required to add
 * a feature key; a grant is just the string appearing in a user's array.
 */
export const FEATURE_KEYS = ["plaid"] as const;

/** Union of valid sensitive feature keys, e.g. `"plaid"`. */
export type SensitiveFeature = (typeof FEATURE_KEYS)[number];

/**
 * Named accessors for call sites (prefer these over string literals so renames
 * are mechanical and refs are greppable). `satisfies` proves every value is a
 * real key without widening the type.
 */
export const SENSITIVE_FEATURES = {
  /** Plaid financial data: bank accounts, transactions, spend intelligence, ROI. */
  PLAID: "plaid",
} as const satisfies Record<string, SensitiveFeature>;

/** Zod schema for a single feature key — derived from FEATURE_KEYS (no cast). */
export const sensitiveFeatureSchema = z.enum(FEATURE_KEYS);

/** All known feature keys as a runtime array (for iteration / display). */
export const ALL_SENSITIVE_FEATURES: readonly SensitiveFeature[] = FEATURE_KEYS;

/**
 * The minimal shape any gate subject must expose: the raw grant array as it
 * comes off the DB (`string[] | null`). Server `AuthenticatedUser`, client
 * `appUser`, and a bare row all satisfy this.
 */
export interface FeatureAccessSubject {
  sensitive_feature_access?: readonly string[] | null;
}

/**
 * THE gate. Returns true iff `subject` has been granted `feature`.
 * `feature` is the typed union, so `hasFeatureAccess(u, "plad")` fails to
 * compile. Unknown keys stored in the DB simply never match (safe-deny).
 */
export function hasFeatureAccess(
  subject: FeatureAccessSubject | null | undefined,
  feature: SensitiveFeature,
): boolean {
  const granted = subject?.sensitive_feature_access;
  if (!granted) return false;
  return granted.includes(feature);
}

/**
 * Validate the raw DB `string[]` into a typed `SensitiveFeature[]`, dropping any
 * unknown/typo keys. Use where features are iterated or displayed; the hot-path
 * gate (`hasFeatureAccess`) does not need this.
 */
export function parseGrantedFeatures(
  raw: readonly string[] | null | undefined,
): SensitiveFeature[] {
  if (!raw) return [];
  return raw.filter((key): key is SensitiveFeature => sensitiveFeatureSchema.safeParse(key).success);
}

// -------------------------------------------------------------------------
// Feature → Meridian chat/MCP tool mapping
//
// Tools that expose a sensitive feature's data must be stripped from a user's
// toolset when they lack the grant (defense-in-depth alongside the API-route
// guard). PLAID_FEATURE_TOOL_NAMES is the canonical set of Plaid/spend tools;
// a build/unit test asserts no plaid/spend/merchant tool escapes this list.
// -------------------------------------------------------------------------

/** Every Meridian tool name that returns or mutates Plaid financial data. */
export const PLAID_FEATURE_TOOL_NAMES: readonly string[] = [
  "list_plaid_transactions",
  "search_plaid_transactions",
  "list_plaid_accounts",
  "list_plaid_connections",
  "list_plaid_statements",
  "get_plaid_statement_download_url",
  "list_merchant_groups",
  "get_spend_insights",
  "get_spend_by_customer",
  "attribute_transaction",
  "set_account_spend_profile",
  "record_spend_recommendation_feedback",
  "list_account_promotions",
  "create_account_promotion",
  "update_account_promotion",
  "list_merchant_flow_classifications",
  "set_merchant_flow_classification",
] as const;

/** Tool names gated by a given sensitive feature. */
export function toolNamesForFeature(feature: SensitiveFeature): readonly string[] {
  switch (feature) {
    case "plaid":
      return PLAID_FEATURE_TOOL_NAMES;
    default:
      return [];
  }
}
